import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Shield, Mail, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Verificar2FA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verify2FA } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const email = location.state?.email;
  const tempToken = location.state?.tempToken;
  const password = location.state?.password;

  useEffect(() => {
    // Redirect if no email or token
    if (!email || !tempToken) {
      navigate('/');
    }
  }, [email, tempToken, navigate]);

  useEffect(() => {
    // Countdown timer for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await verify2FA(email, code, tempToken);

    if (result.success) {
      // Navigate based on role
      const user = result.user;
      if (user.rol === 'administrador') {
        navigate('/admin/dashboard');
      } else if (user.rol === 'tecnico') {
        navigate('/tecnico');
      } else if (user.rol === 'cliente') {
        navigate('/cliente');
      }
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      // Request new code by calling step1 again
      await axios.post(`${API}/auth/login-step1`, {
        email,
        password
      });
      
      setCountdown(60); // 60 seconds cooldown
      alert('Nuevo código enviado a tu correo');
    } catch (err) {
      setError('Error al reenviar código. Por favor, intenta iniciar sesión nuevamente.');
    } finally {
      setResending(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Verificación de Seguridad</CardTitle>
          <CardDescription>
            Hemos enviado un código de 6 dígitos a <strong>{email}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código de Verificación
              </label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
                required
              />
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-2">
                <Mail className="h-3 w-3" />
                El código expira en 10 minutos
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Verificar y Acceder'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-2">
              ¿No recibiste el código?
            </p>
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={resending || countdown > 0}
              className="w-full"
            >
              {resending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reenviando...
                </>
              ) : countdown > 0 ? (
                `Reenviar código (${countdown}s)`
              ) : (
                'Reenviar código'
              )}
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Volver al inicio de sesión
            </Button>
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              <strong>⚠️ Importante:</strong> Por tu seguridad, no compartas este código con nadie.
              Si no solicitaste este código, contacta al administrador inmediatamente.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Verificar2FA;
